/**
 * The race-line easter egg's PURE core: track geometry (Free's sketch: a
 * rounded loop with waisted sides, six arrow gate flags around the
 * perimeter, a double-checkered start/finish at the top) and the race rules:
 *
 *   The line is a guide, not a rail. Each flag is a checkpoint with a
 *   capture radius that must be passed on the correct side, in order, in the
 *   travel direction. Missing a flag voids the lap (no penalty). Hitting a
 *   pole is a crash (sound, no void). The checkered gate arms the clock.
 *
 * No DOM, no audio, no React: everything here is unit-testable by import.
 */

export type Pt = { x: number; y: number };

export type Flag = {
  x: number;
  y: number;
  dirX: number; // travel direction at this flag
  dirY: number;
  req: 1 | -1; // required sign of cross(dir, racer - flag): the correct side
};

export type Track = {
  poly: Pt[]; // sampled closed loop, travel = increasing index (clockwise)
  flags: Flag[]; // in travel order from the gate
  gate: { x: number; y: number; dirX: number; dirY: number; half: number };
  obstacles: Pt[]; // flag poles + the two gate poles (crash checks)
  captureR: number;
  poleR: number;
};

export type RaceState = {
  expected: number; // index of the next flag to clear (0..flags.length)
  lapStart: number | null; // timestamp; null until the gate first arms it
  lastCrash: number;
};

export type RaceEvent =
  | { type: "start" }
  | { type: "flag"; index: number }
  | { type: "crash" }
  | { type: "lap"; valid: boolean; ms: number };

/** Free's sketch, normalized (y down): rounded rectangle, waists mid-sides. */
const CTRL: Pt[] = [
  { x: 0.5, y: 0.1 },
  { x: 0.78, y: 0.11 },
  { x: 0.92, y: 0.22 },
  { x: 0.9, y: 0.38 },
  { x: 0.855, y: 0.5 },
  { x: 0.9, y: 0.62 },
  { x: 0.92, y: 0.78 },
  { x: 0.78, y: 0.9 },
  { x: 0.5, y: 0.92 },
  { x: 0.22, y: 0.9 },
  { x: 0.08, y: 0.78 },
  { x: 0.1, y: 0.62 },
  { x: 0.145, y: 0.5 },
  { x: 0.1, y: 0.38 },
  { x: 0.08, y: 0.22 },
  { x: 0.22, y: 0.11 },
];

/**
 * Where the six flags sit (fraction of the lap from the gate): the four
 * corner apexes and the two side waists, exactly the layout in the sketch.
 */
const FLAG_TS = [0.125, 0.25, 0.375, 0.625, 0.75, 0.875];

const SAMPLES = 260;

function cr(a: number, b: number, c: number, d: number, t: number) {
  return b + 0.5 * t * (c - a + t * (2 * a - 5 * b + 4 * c - d + t * (3 * (b - c) + d - a)));
}

/** Closed uniform Catmull-Rom through the control loop. */
function splineLoop(ctrl: Pt[], samples: number): Pt[] {
  const n = ctrl.length;
  const out: Pt[] = [];
  for (let s = 0; s < samples; s++) {
    const t = (s / samples) * n;
    const i = Math.floor(t);
    const f = t - i;
    const p0 = ctrl[(i - 1 + n) % n];
    const p1 = ctrl[i % n];
    const p2 = ctrl[(i + 1) % n];
    const p3 = ctrl[(i + 2) % n];
    out.push({ x: cr(p0.x, p1.x, p2.x, p3.x, f), y: cr(p0.y, p1.y, p2.y, p3.y, f) });
  }
  return out;
}

export function buildTrack(w: number, h: number): Track {
  const mx = Math.max(46, w * 0.06);
  const myTop = Math.max(64, h * 0.1);
  const myBot = Math.max(46, h * 0.07);
  const poly = splineLoop(CTRL, SAMPLES).map((p) => ({
    x: mx + p.x * (w - 2 * mx),
    y: myTop + ((p.y - 0.1) / 0.82) * (h - myTop - myBot),
  }));

  const dirAt = (i: number): Pt => {
    const a = poly[(i - 1 + SAMPLES) % SAMPLES];
    const b = poly[(i + 1) % SAMPLES];
    const dx = b.x - a.x, dy = b.y - a.y;
    const L = Math.hypot(dx, dy) || 1;
    return { x: dx / L, y: dy / L };
  };

  /**
   * Unit vector toward the CENTRE OF THE BEND at sample i (the second
   * derivative of the curve, taken over a wide stencil since consecutive
   * samples are about a pixel apart). This is where a flag belongs: at a
   * corner it lands inside the loop, at a waist it lands outside, and in
   * both cases it is the pylon the pilot rails AROUND.
   */
  const STENCIL = 7;
  const bendCentre = (i: number): Pt => {
    const a = poly[(i - STENCIL + SAMPLES) % SAMPLES];
    const b = poly[i];
    const c = poly[(i + STENCIL) % SAMPLES];
    const nx = a.x + c.x - 2 * b.x;
    const ny = a.y + c.y - 2 * b.y;
    const L = Math.hypot(nx, ny);
    if (L < 1e-6) {
      const d = dirAt(i); // straight: fall back to the loop's inward normal
      return { x: -d.y, y: d.x };
    }
    return { x: nx / L, y: ny / L };
  };

  const flagOff = 30;
  const flags: Flag[] = FLAG_TS.map((tf) => {
    const i = Math.round(tf * SAMPLES) % SAMPLES;
    const p = poly[i];
    const d = dirAt(i);
    const n = bendCentre(i);
    const fx = p.x + n.x * flagOff, fy = p.y + n.y * flagOff;
    // the pilot passes on whichever side the race line runs, which is now
    // always the OUTSIDE of the bend: the flag is the thing you go around
    const req = Math.sign(d.x * (p.y - fy) - d.y * (p.x - fx)) >= 0 ? 1 : -1;
    return { x: fx, y: fy, dirX: d.x, dirY: d.y, req: req as 1 | -1 };
  });

  const g = poly[0];
  const gd = dirAt(0);
  const half = Math.max(46, Math.min(w, h) * 0.055);
  const gate = { x: g.x, y: g.y, dirX: gd.x, dirY: gd.y, half };
  const obstacles: Pt[] = [
    ...flags.map((f) => ({ x: f.x, y: f.y })),
    { x: g.x - gd.y * half, y: g.y + gd.x * half },
    { x: g.x + gd.y * half, y: g.y - gd.x * half },
  ];

  return {
    poly,
    flags,
    gate,
    obstacles,
    captureR: Math.min(92, Math.max(48, Math.min(w, h) * 0.085)),
    poleR: 15,
  };
}

export function newRaceState(): RaceState {
  return { expected: 0, lapStart: null, lastCrash: 0 };
}

/** One movement step: prev -> cur at time `now` (ms). Returns fired events. */
export function advanceRace(
  track: Track,
  st: RaceState,
  prevX: number,
  prevY: number,
  curX: number,
  curY: number,
  now: number
): RaceEvent[] {
  const evs: RaceEvent[] = [];
  const mvX = curX - prevX, mvY = curY - prevY;

  // next expected flag: inside the capture radius, on the correct side,
  // moving with the track direction
  if (st.expected < track.flags.length) {
    const f = track.flags[st.expected];
    const dx = curX - f.x, dy = curY - f.y;
    if (dx * dx + dy * dy < track.captureR * track.captureR) {
      const side = Math.sign(f.dirX * dy - f.dirY * dx) >= 0 ? 1 : -1;
      const forward = mvX * f.dirX + mvY * f.dirY > 0;
      if (side === f.req && forward) {
        evs.push({ type: "flag", index: st.expected });
        st.expected++;
      }
    }
  }

  // pole strikes (any obstacle), rate-limited
  if (now - st.lastCrash > 900) {
    for (const o of track.obstacles) {
      const dx = curX - o.x, dy = curY - o.y;
      if (dx * dx + dy * dy < track.poleR * track.poleR) {
        st.lastCrash = now;
        evs.push({ type: "crash" });
        break;
      }
    }
  }

  // start/finish: forward crossing of the gate segment
  const g = track.gate;
  const sPrev = (prevX - g.x) * g.dirX + (prevY - g.y) * g.dirY;
  const sCur = (curX - g.x) * g.dirX + (curY - g.y) * g.dirY;
  if (sPrev < 0 && sCur >= 0) {
    const lat = (curX - g.x) * -g.dirY + (curY - g.y) * g.dirX;
    if (Math.abs(lat) <= g.half) {
      if (st.lapStart === null) {
        evs.push({ type: "start" });
      } else {
        evs.push({ type: "lap", valid: st.expected === track.flags.length, ms: now - st.lapStart });
      }
      st.lapStart = now;
      st.expected = 0;
    }
  }

  return evs;
}

/** "12.48s" under a minute, "1:05.32" over. */
export function fmtLap(ms: number): string {
  if (ms < 60000) return (ms / 1000).toFixed(2) + "s";
  const m = Math.floor(ms / 60000);
  const s = (ms - m * 60000) / 1000;
  return m + ":" + s.toFixed(2).padStart(5, "0");
}
